import { CircleAlert } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useId } from 'react';
import { cn } from '@/lib/cn';
import { Label } from './label';

interface FieldRenderProps {
  id: string;
  'aria-invalid': boolean | undefined;
  'aria-describedby': string | undefined;
}

export interface FieldProps {
  label: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
  className?: string;
  optional?: boolean;
  children: (props: FieldRenderProps) => ReactNode;
}

/** Label + control + hint/error wiring with correct aria attributes. */
export function Field({ label, error, hint, className, optional, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id} className="flex items-center justify-between">
        <span>{label}</span>
        {optional && <span className="text-xs font-normal text-fg-subtle">Optional</span>}
      </Label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
      <AnimatePresence initial={false}>
        {error && (
          <motion.p
            key="error"
            id={errorId}
            role="alert"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16 }}
            className="flex items-center gap-1.5 text-xs font-medium text-danger"
          >
            <CircleAlert className="size-3.5 shrink-0" aria-hidden />
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
